import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import { joinClassNames } from 'utils/cssHelper';

export const ConfirmationDialogTypes = {
  DELETE: 'delete',
  CONFIRM: 'confirm',
  WARNING: 'warning',
  INFO: 'info',
};

const SubmitButtonTitles = {
  [ConfirmationDialogTypes.DELETE]: 'Delete',
  [ConfirmationDialogTypes.CONFIRM]: 'Confirm',
  [ConfirmationDialogTypes.WARNING]: 'OK',
  [ConfirmationDialogTypes.INFO]: 'OK',
};

const DefaultDialogTitles = {
  [ConfirmationDialogTypes.DELETE]: 'Are you sure you want to delete this item?',
  [ConfirmationDialogTypes.CONFIRM]: 'Are you sure you want to confirm this item?',
  [ConfirmationDialogTypes.WARNING]: 'Warning',
  [ConfirmationDialogTypes.INFO]: 'Info',
};

const DefaultDialogDescriptions = {
  [ConfirmationDialogTypes.DELETE]: 'This action cannot be undone.',
  [ConfirmationDialogTypes.CONFIRM]: 'This action cannot be undone.',
  [ConfirmationDialogTypes.WARNING]: 'I confirm this',
  [ConfirmationDialogTypes.INFO]: 'Info is clear for me',
};

const DefaultDialogIconColor = {
  [ConfirmationDialogTypes.DELETE]: 'red',
  [ConfirmationDialogTypes.CONFIRM]: 'green',
  [ConfirmationDialogTypes.WARNING]: 'red',
  [ConfirmationDialogTypes.INFO]: 'blue',
};

export const ConfirmationDialog = ({
  type,
  item,
  title,
  description,
  isOpen,
  closeModal,
  onSubmit,
}) => {
  const dialogType = type || ConfirmationDialogTypes.DELETE;

  const submitButtonTitle = SubmitButtonTitles[dialogType];

  const cancelButtonRef = useRef(null);
  const { dataItem, linkColumnProps } = item || { dataItem: null, linkColumnProps: null };

  const getConfirmationTitle = () => {
    if (linkColumnProps && typeof linkColumnProps.confirmationTitle === 'string') {
      return linkColumnProps.confirmationTitle;
    }

    return title || DefaultDialogTitles[dialogType];
  };
  const getConfirmationDescriptjon = () => {
    if (linkColumnProps && typeof linkColumnProps.confirmationDescription === 'string') {
      return linkColumnProps.confirmationDescription;
    }

    return description || DefaultDialogDescriptions[dialogType];
  };

  const onConfirmClicked = () => {
    if (linkColumnProps && linkColumnProps.onClick) {
      linkColumnProps.onClick(dataItem);
    } else if (onSubmit) {
      onSubmit(item);
    }

    closeModal();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-50 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={closeModal}
      >
        <div className="min-h-screen pt-4 px-4 pb-4 text-center block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="inline-block align-middle h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative inline-block bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div
                  className={joinClassNames(
                    'mx-auto flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10',
                    `bg-${DefaultDialogIconColor[dialogType]}-100`,
                  )}
                >
                  <ExclamationIcon
                    className={joinClassNames(
                      'h-6 w-6',
                      `text-${DefaultDialogIconColor[dialogType]}-600`,
                    )}
                    aria-hidden="true"
                  />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                    {getConfirmationTitle()}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">{getConfirmationDescriptjon()}</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={onConfirmClicked}
                >
                  {submitButtonTitle}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  onClick={closeModal}
                  ref={cancelButtonRef}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
